import { useTranslation } from "@repo/shared/i18n";

import { GAME_NAME } from "./game";

/*
  Page titles and descriptions for the content pages. HeroSection renders these
  as the visible h1 and subtitle as well as the meta tags.

  The strings live in each page's i18n namespace under `seo.*`. Everything is
  resolved through hooks: this module is imported before `initI18n`, so nothing
  here may call `t()` at module scope.
*/

/*
  `title` and `description` feed HeroSection's helmetTitle/helmetDescription,
  which are both the meta tags and the visible h1/subtitle. `pageTitle` and
  `subtitle` override one side of that split where a page wants different
  wording on the page than in search results.
*/
export type PageCopy = {
  title: string;
  description: string;
  pageTitle?: string;
  subtitle?: string;
};

export type SeoPage =
  | "sbcs"
  | "squadBuilder"
  | "evolutionsPlayers"
  | "evolutionsOverview"
  | "evolutionStates"
  | "myEvolutionsPlayers"
  | "myEvolutionsSaved"
  | "tactics"
  | "myClub"
  | "cheapestItemScore"
  | "gallery"
  | "galleryLevels"
  | "objectives";

export const useSeoCopy = (page: SeoPage): PageCopy => {
  const { t: tSbcs } = useTranslation("sbcs");
  const { t: tEvo } = useTranslation("evolutions");
  const { t: tTactics } = useTranslation("tactics");
  const { t: tMyClub } = useTranslation("myClub");
  const { t: tSquadBuilder } = useTranslation("squadBuilder");
  const { t: tCheapest } = useTranslation("cheapestItemScore");
  const { t: tGallery } = useTranslation("gallery");
  const { t: tObjectives } = useTranslation("objectives");
  const game = GAME_NAME;

  switch (page) {
    case "cheapestItemScore":
      return {
        title: tCheapest("seo.title", { game }),
        pageTitle: tCheapest("seo.pageTitle", { game }),
        description: tCheapest("seo.description", { game }),
      };
    case "gallery":
      return {
        title: tGallery("seo.title", { game }),
        pageTitle: tGallery("seo.pageTitle", { game }),
        description: tGallery("seo.description", { game }),
        subtitle: tGallery("seo.subtitle", { game }),
      };
    case "galleryLevels":
      return {
        title: tGallery("seo.levelsTitle", { game }),
        pageTitle: tGallery("seo.levelsPageTitle", { game }),
        description: tGallery("seo.levelsDescription", { game }),
        subtitle: tGallery("seo.levelsSubtitle", { game }),
      };
    case "squadBuilder":
      return {
        title: tSquadBuilder("seo.title", { game }),
        description: tSquadBuilder("seo.description", { game }),
      };
    case "sbcs":
      return {
        title: tSbcs("seo.title", { game }),
        description: tSbcs("seo.description", { game }),
      };
    case "evolutionsPlayers":
      return {
        title: tEvo("seo.players.title", { game }),
        description: tEvo("seo.players.description", { game }),
        subtitle: tEvo("seo.players.subtitle", { game }),
      };
    case "evolutionsOverview":
      return {
        title: tEvo("seo.overview.title", { game }),
        description: tEvo("seo.overview.description", { game }),
        subtitle: tEvo("seo.overview.subtitle", { game }),
      };
    case "evolutionStates":
      return {
        title: tEvo("seo.statesGuide.title", { game }),
        pageTitle: tEvo("seo.statesGuide.pageTitle", { game }),
        description: tEvo("seo.statesGuide.description", { game }),
        subtitle: tEvo("seo.statesGuide.subtitle", { game }),
      };
    case "myEvolutionsPlayers":
      return {
        title: tEvo("seo.myPlayers.title", { game }),
        pageTitle: tEvo("seo.myPlayers.pageTitle", { game }),
        description: tEvo("seo.myPlayers.description", { game }),
        subtitle: tEvo("seo.myPlayers.subtitle", { game }),
      };
    case "myEvolutionsSaved":
      return {
        title: tEvo("seo.mySaved.title", { game }),
        pageTitle: tEvo("seo.mySaved.pageTitle", { game }),
        description: tEvo("seo.mySaved.description", { game }),
        subtitle: tEvo("seo.mySaved.subtitle", { game }),
      };
    case "tactics":
      return {
        title: tTactics("seo.title", { game }),
        description: tTactics("seo.description", { game }),
      };
    case "myClub":
      return {
        title: tMyClub("seo.title", { game }),
        description: tMyClub("seo.description", { game }),
      };
    case "objectives":
      return {
        title: tObjectives("seo.title", { game }),
        description: tObjectives("seo.description", { game }),
      };
  }
};

/*
  Rare is the base card every player has, so it says nothing in a heading. Any
  other version is what people search for and stays.
*/
export const headingVersionName = (versionName?: string) =>
  versionName && versionName.trim().toLowerCase() !== "common"
    ? versionName
    : undefined;

/*
  Detailed player pages. The title doubles as the visible h1, and the
  description doubles as the visible subtitle — HeroSection falls back to
  helmetDescription when no explicit subtitle is passed.

  The h1 stays short — name, version, edition — while the tab title spells the
  whole thing out for search results, rating included.

  Both version and rating are optional and independently so: base cards carry
  no version, and the card itself is undefined on first render while the detail
  request is still in flight. A missing rating picks the shorter title key
  rather than leaving an empty segment between two dashes.
*/
export const usePlayerCopy = ({
  name,
  versionName,
  rating,
  gender,
}: {
  name: string;
  versionName?: string;
  rating?: number;
  gender?: "MALE" | "FEMALE";
}) => {
  const { t } = useTranslation("players");
  const ovr = rating ? t("seo.player.ovr", { rating }) : "";
  const version = headingVersionName(versionName);
  const subject = `${GAME_NAME} ${name}${versionName ? ` ${versionName}` : ""}${
    ovr ? `, ${ovr}` : ""
  }`;
  const player = [name, version].filter(Boolean).join(" ");
  const heading = t("seo.player.heading", { player, game: GAME_NAME });

  return {
    title: heading,
    pageTitle: ovr
      ? t("seo.player.pageTitle", { player, game: GAME_NAME, ovr })
      : t("seo.player.pageTitleNoRating", { player, game: GAME_NAME }),
    description: t(
      gender === "FEMALE"
        ? "seo.player.descriptionFemale"
        : "seo.player.descriptionMale",
      { subject },
    ),
  };
};

/*
  The Evolution Builder names the player that has been picked, so its copy is a
  function of the label. `playerLabel` is empty on the picker itself, which
  keeps its own generic wording.

  A named player reads like the detailed player pages: the edition sits in the
  h1, and the tab adds the brand on top of it.
*/
export const useEvoBuilderCopy = (playerLabel: string) => {
  const { t } = useTranslation("evoBuilder");
  const game = GAME_NAME;
  const subject = playerLabel || t("seo.anyPlayer");
  const heading = playerLabel
    ? t("seo.playerTitle", { player: playerLabel, game })
    : t("seo.title", { game });

  return {
    title: heading,
    pageTitle: playerLabel
      ? t("seo.playerPageTitle", { heading })
      : t("seo.title", { game }),
    description: t("seo.description", { subject, game }),
    subtitle: t("seo.subtitle", { subject, game }),
  };
};
